// Copyright (c) Bentley Systems
import { useCallback, useEffect, useRef, useState } from "react";
import { useToaster } from "@itwin/itwinui-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import {
  resetIModelState,
  selectCreateIModelValue,
  selectDefaultColor,
  selectIntelligentContent,
  setCreateIModelValue,
  setIModelDescription,
  setIModelValueId,
  setIntelligentContent,
  setIsCreatingIModelValue,
  setisUrlValid as setisEntryScreenUrlValid,
  setIsImodelValueDefined,
} from "../app/entry-screen-slice";
import {
  checkInternalServerError,
  getUrlBasedOnEnv,
  isEmptyObject,
  isGuid,
  setErrorState,
} from "../Helpers/helper-functions";
import { IModelValue } from "./Models/imodel-value";
import { mapDataToModels, mapResponseToDTO } from "../Helpers/map-response";
import { setIsToastError } from "../app/is-toast-error-slice";

export type EntryScreenLogicProps = {
  isActive: boolean;
  iTwinId: string | null;
  iModelId: string | null;
};

export async function toggleIntelligentContent(
  iTwinId: string,
  iModelId: string,
  iModelValueId: number,
  intelligentContent: boolean,
  dispatch: any,
  accessToken: string,
) {
  const url = getUrlBasedOnEnv(
    `/api/EntryScreen/itwins/${iTwinId}/imodels/${iModelId}/IModelValue/${iModelValueId}`,
  );
  const runId = crypto.randomUUID();

  try {
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        accept: "text/plain",
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: runId,
      },
      body: JSON.stringify({ intelligentContent: !intelligentContent }),
    });

    if (!response.ok) {
      setErrorState(response.status, dispatch, runId, true);
      return;
    }

    const result = await response.json();
    dispatch(
      setIntelligentContent(result?.intelligentContent ?? !intelligentContent),
    );
  } catch {
    setErrorState(500, dispatch, runId, true);
  }
}

export const EntryScreenLogic: React.FC<EntryScreenLogicProps> = ({
  isActive,
  iTwinId,
  iModelId,
}) => {
  const [isEntryScreenValid, setEntryScreenValid] = useState<boolean>(true);
  const [hasShownToast, setHasShownToast] = useState<boolean>(false);
  const hasCalledApiRef = useRef<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);
  const createIModelValueFlag = useAppSelector(selectCreateIModelValue);
  const intelligentContent = useAppSelector(selectIntelligentContent);
  const defaultColor = useAppSelector(selectDefaultColor);
  const accessToken = useAppSelector(selectAccessToken);
  const dispatch = useAppDispatch();
  const toaster = useToaster();
  const handleIModelValueResponse = useCallback(
    (result: any) => {
      const iModelValues: IModelValue[] = mapDataToModels<IModelValue>(result);
      const iModelValue = mapResponseToDTO<IModelValue, typeof result>(result);

      if (iModelValues.length > 0 || !isEmptyObject(iModelValue)) {
        dispatch(setIsImodelValueDefined(true));
        dispatch(setCreateIModelValue(false));
      } else {
        dispatch(setIsImodelValueDefined(false));
      }
    },
    [dispatch],
  );

  // URL validation for EntryScreen logic
  useEffect(() => {
    if (!isActive) {
      setEntryScreenValid(true);
      setHasShownToast(false);
      hasCalledApiRef.current = ""; // Clear API deduplication cache to allow fresh calls when component reactivates
      return;
    }

    let isValid = true;
    let errorMessage = "";

    if (!iTwinId) {
      errorMessage = "iTwinId query parameter is missing.";
      isValid = false;
    } else if (!isGuid(iTwinId)) {
      errorMessage = "iTwinId is not a valid GUID.";
      isValid = false;
    }

    if (!iModelId) {
      errorMessage = errorMessage
        ? `${errorMessage} iModelId query parameter is missing.`
        : "iModelId query parameter is missing.";
      isValid = false;
    } else if (!isGuid(iModelId)) {
      errorMessage = errorMessage
        ? `${errorMessage} iModelId is not a valid GUID.`
        : "iModelId is not a valid GUID.";
      isValid = false;
    }

    // Reset toast flag when validation becomes valid
    if (isValid && hasShownToast) {
      setHasShownToast(false);
    }

    if (!isValid && errorMessage && !hasShownToast) {
      toaster.negative(errorMessage, {
        hasCloseButton: true,
        type: "persisting",
      });
      setHasShownToast(true);
    }

    setEntryScreenValid(isValid);
    dispatch(setisEntryScreenUrlValid(isValid));
  }, [iTwinId, iModelId, isActive, dispatch, toaster, hasShownToast]);

  // Fetch IModelValue data when EntryScreen is active and valid
  useEffect(() => {
    if (isActive && accessToken && iTwinId && iModelId) {
      // Create a unique key for this API call
      const apiKey = `${iTwinId}-${iModelId}`;

      // Skip if we've already called this API
      if (hasCalledApiRef.current === apiKey) {
        return;
      }

      // Validate again here to avoid dependency issues
      if (!isGuid(iTwinId) || !isGuid(iModelId)) {
        return; // Exit early if invalid
      }

      // Abort previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Reset stale iModel state before fetching new data
      dispatch(resetIModelState());

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      hasCalledApiRef.current = apiKey;

      let responseClone: Response;
      const url = getUrlBasedOnEnv(
        `/api/EntryScreen/IModelValue?iModelId=${iModelId}&iTwinId=${iTwinId}`,
      );
      const runId = crypto.randomUUID();

      fetch(url, {
        method: "GET",
        headers: {
          Authorization: accessToken,
          RunId: runId,
        },
        signal: abortControllerRef.current.signal,
      })
        // NOTE: This GET endpoint is intentionally still on the legacy `Response`
        // envelope ({ isSuccess, result, runId }) and has NOT been migrated to the
        // raw-body / ProblemDetails shape used elsewhere (e.g. the POST below, which
        // reads `data.id` directly). See the TODO on EntryScreenController.GetIModelValue:
        // external consumers (itwin-experience-portal, BECS6.Widgets) still read
        // `data.result`, so the contract must stay enveloped until they are updated.
        // That is why this handler gates on `data.isSuccess` and reads `data.result`.
        .then(async function (response) {
          checkInternalServerError(response.status, dispatch, runId);
          if (response.status === 500) {
            dispatch(setisEntryScreenUrlValid(false));
          }
          responseClone = response.clone();
          return response.json();
        })
        .then(function (data) {
          if (data.isSuccess) {
            // result may be null (e.g. early-return paths) or an empty array when
            // no IModelValue is configured; treat both as "not defined" so the
            // "Allow configuration" button becomes enabled.
            const iModelValues = data.result ?? [];
            handleIModelValueResponse(iModelValues);
            dispatch(setisEntryScreenUrlValid(true));
            dispatch(setIModelDescription(iModelValues[0]?.description));
            dispatch(setIntelligentContent(iModelValues[0]?.intelligentContent));
            dispatch(setIModelValueId(iModelValues[0]?.id));
          } else {
            dispatch(
              setIsToastError({
                status: "",
                action: "",
                message:
                  "Failed to load iModel configuration. If the iModel was recently created, please wait a moment and try again.",
              }),
            );
            setErrorState(responseClone.status, dispatch, data.runId, true);
            dispatch(setisEntryScreenUrlValid(false));
            hasCalledApiRef.current = "";
          }
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            // Reset the ref on error so it can be retried
            hasCalledApiRef.current = "";
          }
        });
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    isActive,
    accessToken,
    iTwinId,
    iModelId,
    dispatch,
    handleIModelValueResponse,
  ]);

  const getSaveJson = useCallback(() => {
    return JSON.stringify({
      intelligentContent,
      defaultColor,
    });
  }, [intelligentContent, defaultColor]);

  const createIModelValueAction = useCallback(() => {
    if (isEntryScreenValid && accessToken) {
      // Show loading state
      dispatch(setIsCreatingIModelValue(true));

      // Create abort controller for this request
      const createAbortController = new AbortController();

      const url = getUrlBasedOnEnv(
        `/api/EntryScreen/itwins/${iTwinId}/imodels/${iModelId}/IModelValue`,
      );
      const runId = crypto.randomUUID();
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runId,
        },
        body: getSaveJson(),
        signal: createAbortController.signal,
      })
        // NOTE: Unlike the GET above, this POST endpoint HAS been migrated to the
        // raw-body / ProblemDetails shape: it returns the IModelValue object directly
        // (e.g. `data.id`) and signals errors via a non-ok status, not an envelope.
        .then(async function (response) {
          if (!response.ok) {
            setErrorState(response.status, dispatch, response.headers.get("X-Run-Id") ?? runId);
            return;
          }
          return response.json();
        })
        .then(function (data) {
          if (!data) {
            return;
          }
          // POST returns a single object, dispatch state updates directly
          dispatch(setIsImodelValueDefined(true));
          dispatch(setCreateIModelValue(false));
          if (data.id) {
            dispatch(setIModelValueId(data.id));
            dispatch(setIntelligentContent(data.intelligentContent));
            dispatch(setIModelDescription(data.description));
          }
        })
        .catch((error) => {
          if (error?.name !== "AbortError") {
            setErrorState(500, dispatch, runId, true);
          }
        })
        .finally(() => {
          dispatch(setIsCreatingIModelValue(false));
          dispatch(setCreateIModelValue(false));
        });

      // Return cleanup function to abort request if needed
      return () => {
        createAbortController.abort();
      };
    }
  }, [
    isEntryScreenValid,
    accessToken,
    getSaveJson,
    dispatch,
    iTwinId,
    iModelId,
  ]);

  // Handle createIModelValue flag
  useEffect(() => {
    if (isActive && createIModelValueFlag) {
      const cleanup = createIModelValueAction();

      // Return cleanup function to abort on unmount or re-execution
      return cleanup;
    }
  }, [createIModelValueFlag, isActive, createIModelValueAction]);

  // Early return after all hooks if not active
  if (!isActive) {
    return null;
  }

  // This component doesn't render anything, it just handles logic
  return null;
};
